import type { ComputedRef } from "vue";
import type { FormItems } from "@/composables/forms/formTypes";
import { computed } from "vue";
import { Override_Options_Enum } from "gql/api";
import {
  getFieldName,
  getFieldValue,
  updateGasSeparationFields,
  type Stack
} from "@/composables/forms/stackAwareFields";

/**
 * Helper function to compare values with 2 decimal precision
 */
export function valuesEqual(value1: number, value2: number): boolean {
  return Math.round(value1 * 100) === Math.round(value2 * 100);
}

/**
 * Helper function to check if a gas separation type is overridden
 */
export function isGasSeparationOverridden(
  form: FormItems["parameters"] | FormItems["performanceEvaluation"],
  overrideTypeFieldKey:
    | "naturalGasSepOverrideType"
    | "intakeGasSepOverrideType",
  percentFieldKey: "naturalGasSepPercent" | "intakeGasSepPercent",
  calculatedValue: ComputedRef<number>,
  packerInstalled: ComputedRef<boolean>,
  stack?: Stack
): boolean {
  if (packerInstalled.value) {
    return false;
  }

  // Check if override type is Override
  const overrideType = getFieldValue(
    form.data.value,
    overrideTypeFieldKey,
    stack
  ) as Override_Options_Enum | null | undefined;

  if (overrideType !== Override_Options_Enum.Override) {
    return false;
  }

  // Only show as overridden if the actual stored value differs from calculated
  const storedPercent = getFieldValue(
    form.data.value,
    percentFieldKey,
    stack
  ) as number | null | undefined;

  // If no stored percent value, not overridden
  if (storedPercent === null || storedPercent === undefined) {
    return false;
  }

  // Compare stored value with calculated value (2 decimal precision)
  return !valuesEqual(storedPercent, calculatedValue.value);
}

/**
 * Helper function to get current gas separation value (calculated or overridden)
 */
export function getCurrentGasSeparationValue(
  form: FormItems["parameters"] | FormItems["performanceEvaluation"],
  calculatedValue: ComputedRef<number>,
  isOverridden: ComputedRef<boolean>,
  percentFieldKey: "naturalGasSepPercent" | "intakeGasSepPercent",
  packerInstalled: ComputedRef<boolean>,
  stack?: Stack
): number {
  // When packer is installed, always show calculated value (ignore any override)
  if (packerInstalled.value) {
    return calculatedValue.value;
  }

  if (isOverridden.value) {
    const percentValue = getFieldValue(
      form.data.value,
      percentFieldKey,
      stack
    ) as number | null | undefined;
    return percentValue ?? calculatedValue.value;
  }
  return calculatedValue.value;
}

/**
 * Helper function to handle gas separation value changes
 */
export function handleGasSeparationValueChange(
  form: FormItems["parameters"] | FormItems["performanceEvaluation"],
  newValue: number | null,
  calculatedValue: ComputedRef<number>,
  overrideTypeFieldKey:
    | "naturalGasSepOverrideType"
    | "intakeGasSepOverrideType",
  percentFieldKey: "naturalGasSepPercent" | "intakeGasSepPercent",
  stack?: Stack
) {
  // When user deletes all input (null or undefined), reset to calculated mode
  // This ensures the input shows the calculated value, not 0
  const overrideTypeFieldName = getFieldName(overrideTypeFieldKey, stack);
  const percentFieldName = getFieldName(percentFieldKey, stack);

  if (newValue === null || newValue === undefined || isNaN(newValue)) {
    updateGasSeparationFields(form, {
      [overrideTypeFieldName]: Override_Options_Enum.Calculate,
      [percentFieldName]: null
    });
    return;
  }

  // Check if the new value differs from calculated (2 decimal precision)
  const valueDiffers = !valuesEqual(newValue, calculatedValue.value);

  if (valueDiffers) {
    // User edited the value, switch to override mode
    updateGasSeparationFields(form, {
      [overrideTypeFieldName]: Override_Options_Enum.Override,
      [percentFieldName]: newValue
    });
  } else {
    // Value matches calculated exactly, always switch back to calculate mode
    // This ensures that if user clicks into input without editing, it stays in Calculate mode
    // The percent field is cleared because it's not used in calculate mode - only the override type matters
    updateGasSeparationFields(form, {
      [overrideTypeFieldName]: Override_Options_Enum.Calculate,
      [percentFieldName]: null
    });
  }
}

/**
 * Helper function to reset gas separation to calculated
 */
export function resetGasSeparationToCalculated(
  form: FormItems["parameters"] | FormItems["performanceEvaluation"],
  overrideTypeFieldKey:
    | "naturalGasSepOverrideType"
    | "intakeGasSepOverrideType",
  percentFieldKey: "naturalGasSepPercent" | "intakeGasSepPercent",
  stack?: Stack
) {
  const overrideTypeFieldName = getFieldName(overrideTypeFieldKey, stack);
  const percentFieldName = getFieldName(percentFieldKey, stack);
  updateGasSeparationFields(form, {
    [overrideTypeFieldName]: Override_Options_Enum.Calculate,
    [percentFieldName]: null
  });
}

/**
 * Helper function to create override tooltip
 */
export function createOverrideTooltip(calculatedValue: number): string {
  const { format } = Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2
  });
  const formattedValue = format(calculatedValue);
  return `Calculated value is overridden. Calculated value would be ${formattedValue}%`;
}

/**
 * Composable for managing gas separation override state
 */
export function useGasSeparationOverride(
  form: FormItems["parameters"] | FormItems["performanceEvaluation"],
  overrideTypeFieldKey:
    | "naturalGasSepOverrideType"
    | "intakeGasSepOverrideType",
  percentFieldKey: "naturalGasSepPercent" | "intakeGasSepPercent",
  calculatedValue: ComputedRef<number>,
  packerInstalled: ComputedRef<boolean>,
  stack?: Stack
) {
  const isOverridden = computed(() =>
    isGasSeparationOverridden(
      form,
      overrideTypeFieldKey,
      percentFieldKey,
      calculatedValue,
      packerInstalled,
      stack
    )
  );

  const currentValue = computed(() =>
    getCurrentGasSeparationValue(
      form,
      calculatedValue,
      isOverridden,
      percentFieldKey,
      packerInstalled,
      stack
    )
  );

  const overrideTooltip = computed(() =>
    createOverrideTooltip(calculatedValue.value)
  );

  const handleValueChange = (newValue: number | null) => {
    handleGasSeparationValueChange(
      form,
      newValue,
      calculatedValue,
      overrideTypeFieldKey,
      percentFieldKey,
      stack
    );
  };

  const resetToCalculated = () => {
    resetGasSeparationToCalculated(
      form,
      overrideTypeFieldKey,
      percentFieldKey,
      stack
    );
  };

  return {
    isOverridden,
    currentValue,
    overrideTooltip,
    handleValueChange,
    resetToCalculated
  };
}
